import React from "react";
import { Box, Typography, styled, keyframes } from "@mui/material";

import CircularIconLayout from "./circularicon";

// Automatic 3D rotation animation
const rotateAnimation = keyframes`
  0% {
    transform: rotateY(0deg) rotateX(0deg);
  }
  50% {
    transform: rotateY(10deg) rotateX(5deg);
  }
  100% {
    transform: rotateY(0deg) rotateX(0deg);
  }
`;

// Wrapper for the entire component including overlay and collage
const CollageWrapper = styled(Box)({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "black",
});

// The background overlay
const BackgroundOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(35, 65, 82, 0.8)", // Dark transparent overlay
  zIndex: 3,
});

// Foreground text overlay centered in the container
const CenteredTextOverlay = styled(Box)({
  width: "100%",
  // maxWidth: "600px",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  color: "#fff",
  zIndex: 3,
  textAlign: "center",
  fontSize: "2.5rem",
  fontWeight: 700,
  "@media (max-width: 768px)": { fontSize: "2rem" },
  "@media (max-width: 480px)": { fontSize: "1.5rem" },
});

// Collage container for images that spans the full width of the screen
const CollageContainer = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  width: "100%", // Makes sure the collage spans full width
  height: "100%",
  position: "relative",
  zIndex: 2, // Ensures the collage is between the two overlays
  "@media (max-width: 1024px)": {
    gridTemplateColumns: "repeat(6, 1fr)",
  },
  "@media (max-width: 768px)": {
    gridTemplateColumns: "repeat(4, 1fr)",
  },
  "@media (max-width: 480px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
});

// Styles for each image with automatic 3D animation and no gap
const CollageImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover", // Ensures images cover their area neatly
  animation: `${rotateAnimation} 5s infinite ease-in-out`,
  border: "none", // Removes any border or gap between images
});

const ResearchHeader: React.FC = () => {
  return (
    <CollageWrapper>
      {/* Background overlay */}
      <BackgroundOverlay
        sx={{
          backgroundColor: {
            xs: "rgba(35, 65, 82, 1)", // For extra small screens
            sm: "rgba(35, 65, 82, 0.8)", // For small screens and up
          },
        }}
      />

      {/* Central text overlay */}
      <CenteredTextOverlay>
        <Typography
          variant="h3"
          component="h3"
          color={'white'} 
          sx={{ marginBottom: "10px", fontSize: { xs: "2.5rem", md: "3rem" } }}
          className="component"
        >
          Research Methodology
        </Typography>
        <Typography
        color={'rgba(255, 255, 255, 0.8)'} 
          variant="body1"
          component="p"
          sx={{
            width: { xs: "350px", md: "45%" },
            margin: "0 auto",
          }}
          className="component"
        >
          This research methodology outlines the systematic approach used to
          investigate [specific topic, e.g., assignment writing services, data
          analytics, etc.], including the selection of participants, data
          collection methods, and analytical techniques. By employing a robust
          framework, this study aims to ensure the validity and reliability of
          the findings.
        </Typography>
      </CenteredTextOverlay>

      {/* Collage Container */}
      <CollageContainer className="component">
        {/* Images arranged in a seamless grid with no gap */}
        <Box gridColumn="span 6">
          <CollageImage src="matt-2.jpg" alt="Office Meeting" />
        </Box>
        <Box gridColumn="span 6">
          <CollageImage src="team1.jpg" alt="Collaborative Workspace" />
        </Box>

        <Box gridColumn="span 3">
          <CollageImage src="team2.jpg" alt="Working Team" />
        </Box>
        <Box gridColumn="span 3">
          <CollageImage src="team4.jpg" alt="Designers Working" />
        </Box>
        <Box gridColumn="span 6">
          <CollageImage src="team5.jpg" alt="Modern Building" />
        </Box>
      </CollageContainer>
      <CircularIconLayout />
    </CollageWrapper>
  );
};

export default ResearchHeader;
